/**
 * Excel export functionality specifically for PinnedClientsList
 * Extracted from PinnedClientsList.js to improve maintainability
 */

import ExportUtils from './ExportUtils';
import { getExcelTierColors } from '../../../services/TierColorService';
import { parseCurrencyToNumber } from '../../../services/RevenueCalculationService';

/**
 * Custom hook for Excel export functionality in PinnedClientsList
 * @returns {Object} Object containing exportToExcel function
 */
export const usePinnedListExcelExport = () => {
  
  /**
   * Enhanced Excel export using xlsx-js-style with tier-based styling
   * @param {Array} clients - Array of client objects to export
   * @param {string} portfolioType - Type of portfolio ('manager' or 'custom')
   * @param {string} activeManager - Active manager name (if applicable)
   * @param {Function} calculateTotalRevenue - Function to calculate total revenue
   * @param {Function} showNotification - Function to show notification messages
   * @returns {Promise<void>}
   */
  const exportToExcel = async (clients, portfolioType, activeManager, calculateTotalRevenue, showNotification) => {
    try {
      // Import xlsx-js-style for styled export (write-only, no security risk)
      const XLSX = await import('xlsx-js-style');
      
      // Get tier colors from centralized service
      const tierColors = getExcelTierColors();

      // Calculate summary statistics
      const totalRevenue = calculateTotalRevenue();
      const tierCounts = {};
      clients.forEach(client => {
        const tier = client['Tier'] || 'Unknown';
        tierCounts[tier] = (tierCounts[tier] || 0) + 1;
      });

      // Calculate year-specific totals for summary section
      const currentYear = new Date().getFullYear().toString();
      const yearRoomsColumn = `${currentYear} Rooms`;
      const yearRNColumn = `${currentYear} RN`;
      const yearRevenueColumn = `${currentYear} Revenue`;
      
      let totalYearRooms = 0;
      let totalYearRevenue = 0;
      let totalYearRN = 0;
      let totalSalesGoal = 0;
      let hasSalesGoalData = false;
      
      clients.forEach(client => {
        // Sum year-specific rooms
        const yearRooms = parseInt(client[yearRoomsColumn]) || 0;
        totalYearRooms += yearRooms;
        
        // Sum year-specific revenue
        const yearRevenue = parseCurrencyToNumber(client[yearRevenueColumn]) || 0;
        totalYearRevenue += yearRevenue;
        
        // Sum year-specific RN values (not collect individual values)
        const yearRN = client[yearRNColumn];
        if (yearRN && !isNaN(yearRN)) {
          totalYearRN += parseInt(yearRN, 10);
        }
        
        // Sum Sales Goal
        const salesGoal = parseCurrencyToNumber(client['Sales Goal']) || 0;
        if (salesGoal > 0) {
          totalSalesGoal += salesGoal;
          hasSalesGoalData = true;
        }
      });

      // Prepare styled data array
      const styledData = [];
      
      // TITLE ROW - Blue header spanning full width (11 columns)
      const titleRow = [];
      for (let i = 0; i < 11; i++) {
        if (i === 0) {
          titleRow.push({
            v: 'CLIENT PORTFOLIO EXPORT',
            t: 's',
            s: {
              font: { bold: true, sz: 16, color: { rgb: '0074D9' }, name: 'Segoe UI' },
              fill: { fgColor: { rgb: 'FFFFFF' } },
              alignment: { horizontal: 'center', vertical: 'center' }
            }
          });
        } else {
          titleRow.push({ v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } } } });
        }
      }
      styledData.push(titleRow);
      
      // Second title row for merging (empty row that will be merged with title)
      const titleRow2 = [];
      for (let i = 0; i < 11; i++) {
        titleRow2.push({ v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } } } });
      }
      styledData.push(titleRow2);
      
      // Empty row for spacing
      const spacingRow = [];
      for (let i = 0; i < 11; i++) {
        spacingRow.push({ v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } } } });
      }
      styledData.push(spacingRow);
      
      // Define border style for header section
      const headerBorder = {
        top: { style: 'thin', color: { rgb: 'E0E0E0' } },
        bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
        left: { style: 'thin', color: { rgb: 'E0E0E0' } },
        right: { style: 'thin', color: { rgb: 'E0E0E0' } }
      };
      
      // HORIZONTAL HEADER SECTION - Row 1: Basic Info (with borders)
      const headerRow1 = [
        { v: 'Report Generated:', t: 's', s: { font: { bold: true, sz: 10, name: 'Segoe UI' }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: new Date().toLocaleString(), t: 's', s: { font: { sz: 10, name: 'Segoe UI' }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: 'Total Clients:', t: 's', s: { font: { bold: true, sz: 10, name: 'Segoe UI' }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: clients.length, t: 'n', s: { font: { sz: 10, name: 'Segoe UI' }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: 'Total Revenue:', t: 's', s: { font: { bold: true, sz: 10, name: 'Segoe UI' }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: totalRevenue, t: 's', s: { font: { sz: 10, name: 'Segoe UI', bold: true, color: { rgb: '0074D9' } }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } }, border: headerBorder } },
        { v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } }, border: headerBorder } },
        { v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } }, border: headerBorder } },
        { v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } }, border: headerBorder } },
        { v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } }, border: headerBorder } }
      ];
      styledData.push(headerRow1);
      
      // HORIZONTAL HEADER SECTION - Row 2: Year-specific data (with borders)
      const yearRow = [
        { v: `${currentYear} Rooms:`, t: 's', s: { font: { bold: true, sz: 10, name: 'Segoe UI' }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: totalYearRooms, t: 'n', s: { font: { sz: 10, name: 'Segoe UI', bold: true, color: { rgb: '0074D9' } }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: `${currentYear} RN:`, t: 's', s: { font: { bold: true, sz: 10, name: 'Segoe UI' }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: totalYearRN, t: 'n', s: { font: { sz: 10, name: 'Segoe UI', bold: true, color: { rgb: '0074D9' } }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: `${currentYear} Revenue:`, t: 's', s: { font: { bold: true, sz: 10, name: 'Segoe UI' }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: `$${totalYearRevenue.toLocaleString()}`, t: 's', s: { font: { sz: 10, name: 'Segoe UI', bold: true, color: { rgb: '0074D9' } }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: 'Sales Goal:', t: 's', s: { font: { bold: true, sz: 10, name: 'Segoe UI' }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: hasSalesGoalData ? `$${totalSalesGoal.toLocaleString()}` : 'n/a', t: 's', s: { font: { sz: 10, name: 'Segoe UI', bold: true, color: { rgb: '0074D9' } }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: headerBorder } },
        { v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } }, border: headerBorder } },
        { v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } }, border: headerBorder } },
        { v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } }, border: headerBorder } }
      ];
      styledData.push(yearRow);
      
      // HORIZONTAL HEADER SECTION - Row 3: Tier Distribution (with borders) - VIP, Tier 1, Tier 2, Tier 3, Tier n/a
      const tierOrder = ['VIP', '1', '2', '3', 'n/a'];
      const tierRow = [];
      
      // Add "TIER DISTRIBUTION" label
      tierRow.push({ 
        v: 'TIER DISTRIBUTION', 
        t: 's', 
        s: { 
          font: { bold: true, sz: 10, color: { rgb: '0074D9' }, name: 'Segoe UI' }, 
          fill: { fgColor: { rgb: 'FFFFFF' } }, 
          alignment: { horizontal: 'center', vertical: 'center' },
          border: headerBorder
        } 
      });
      
      // Add tier data horizontally in specified order
      let colIndex = 1;
      tierOrder.forEach(tier => {
        if (colIndex < 11 && tierCounts[tier]) { // Only add if tier exists and we don't exceed table width
          const tierColor = tierColors[tier] || tierColors['Unknown'];
          tierRow.push({ 
            v: `Tier ${tier}: ${tierCounts[tier]}`, 
            t: 's', 
            s: { 
              font: { bold: true, sz: 10, name: 'Segoe UI', color: { rgb: tierColor.font.substring(2) } },
              fill: { fgColor: { rgb: tierColor.bg.substring(2) } },
              alignment: { horizontal: 'center', vertical: 'center' },
              border: headerBorder
            }
          });
          colIndex++;
        }
      });
      
      // Fill remaining columns
      while (colIndex < 11) {
        tierRow.push({ v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } }, border: headerBorder } });
        colIndex++;
      }
      styledData.push(tierRow);
      
      // Empty row separator (yellow highlighted - remove vertical borders)
      const emptyRow = [];
      for (let i = 0; i < 11; i++) {
        emptyRow.push({ 
          v: '', 
          t: 's', 
          s: { 
            fill: { fgColor: { rgb: 'FFFFFF' } },
            border: {
              top: { style: 'thin', color: { rgb: 'E0E0E0' } },
              bottom: { style: 'thin', color: { rgb: 'E0E0E0' } }
              // No left/right borders
            }
          }
        });
      }
      styledData.push(emptyRow);

      // COLUMN HEADERS (keep original columns only)
      const headers = [
        'Client Name', 'Country', 'Total Rooms', 'Manager', 'Tier',
        'Total Revenue', 'Last Booking Revenue', 'Last Booked',
        'Time Zone', 'Last Booked Conference', 'Total RN'
      ];
      
      const styledHeaders = headers.map(header => ({
        v: header,
        t: 's',
        s: {
          font: { bold: true, sz: 11, color: { rgb: '333333' }, name: 'Segoe UI' },
          fill: { fgColor: { rgb: 'F0F4F8' } },
          border: {
            top: { style: 'thin', color: { rgb: 'E0E0E0' } },
            bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
            left: { style: 'thin', color: { rgb: 'E0E0E0' } },
            right: { style: 'thin', color: { rgb: 'E0E0E0' } }
          },
          alignment: { horizontal: 'center', vertical: 'center' }
        }
      }));
      
      styledData.push(styledHeaders);
      
      // CLIENT DATA ROWS with tier-based styling AND proper data types for sorting
      clients.forEach((client, index) => {
        const tier = client['Tier'] || 'Unknown';
        const tierColor = tierColors[tier] || tierColors['Unknown'];
        const isAlternateRow = index % 2 === 1;
        
        // Prepare row data with proper types for sorting using centralized service
        const rowData = [
          { value: client.client_name, type: 's' },                                    // Text
          { value: client.country, type: 's' },                                       // Text
          { value: parseInt(client.group_size) || 0, type: 'n' },                    // Number
          { value: client.conference_manager, type: 's' },                           // Text
          { value: tier, type: 's' },                                                // Text
          { value: parseCurrencyToNumber(client['Total Revenue']), type: 'n' },       // Number for sorting
          { value: parseCurrencyToNumber(client['Last Booking Revenue']), type: 'n' }, // Number for sorting
          { value: parseInt(client['Last Booked']) || 0, type: 'n' },               // Number (year)
          { value: client['Time zone'] || 'N/A', type: 's' },                       // Text
          { value: client['Segment'] || 'N/A', type: 's' },                         // Text
          { value: client['RN'] || 'N/A', type: 's' }                               // Text
        ];
        
        const styledRow = rowData.map((cellData, colIndex) => {
          const baseStyle = {
            font: { sz: 10, name: 'Segoe UI' },
            border: {
              top: { style: 'thin', color: { rgb: 'EEEEEE' } },
              bottom: { style: 'thin', color: { rgb: 'EEEEEE' } },
              left: { style: 'thin', color: { rgb: 'EEEEEE' } },
              right: { style: 'thin', color: { rgb: 'EEEEEE' } }
            },
            alignment: { horizontal: 'center', vertical: 'center' }
          };
          
          // Tier column (index 4) - special tier styling
          if (colIndex === 4) {
            return {
              v: cellData.value,
              t: cellData.type,
              s: {
                ...baseStyle,
                font: { ...baseStyle.font, bold: true, color: { rgb: tierColor.font.substring(2) } },
                fill: { fgColor: { rgb: tierColor.bg.substring(2) } },
                alignment: { horizontal: 'center', vertical: 'center' }
              }
            };
          }
          // Revenue columns (5, 6) - number values with currency formatting
          else if (colIndex === 5 || colIndex === 6) {
            return {
              v: cellData.value,
              t: cellData.type,
              s: {
                ...baseStyle,
                font: { ...baseStyle.font, bold: true, color: { rgb: '0074D9' } },
                alignment: { horizontal: 'center', vertical: 'center' },
                numFmt: '"$"#,##0.00'
              }
            };
          }
          // Group size column (2) - number formatting
          else if (colIndex === 2) {
            return {
              v: cellData.value,
              t: cellData.type,
              s: {
                ...baseStyle,
                alignment: { horizontal: 'center', vertical: 'center' },
                numFmt: '0'
              }
            };
          }
          // Last Booked column (7) - year formatting
          else if (colIndex === 7) {
            return {
              v: cellData.value,
              t: cellData.type,
              s: {
                ...baseStyle,
                alignment: { horizontal: 'center', vertical: 'center' },
                numFmt: '0'
              }
            };
          }
          // Email column (9) - link styling
          else if (colIndex === 9) {
            return {
              v: cellData.value,
              t: cellData.type,
              s: {
                ...baseStyle,
                font: { ...baseStyle.font, color: { rgb: '0074D9' }, underline: true },
                alignment: { horizontal: 'center', vertical: 'center' }
              }
            };
          }
          // Regular text columns with alternating row colors
          else {
            return {
              v: cellData.value,
              t: cellData.type,
              s: {
                ...baseStyle,
                fill: isAlternateRow ? { fgColor: { rgb: 'F9F9F9' } } : undefined,
                alignment: { horizontal: 'center', vertical: 'center' }
              }
            };
          }
        });
        
        styledData.push(styledRow);
      });

      // Create worksheet from styled data
      const worksheet = XLSX.utils.aoa_to_sheet(styledData);
      
      // Calculate important row positions
      // New structure: Title row (1) + Title row 2 (1) + Spacing row (1) + Header row 1 (1) + Year row (1) + Tier row (1) + Empty row (1) = 7 rows before table headers
      const headerRowIndex = 7; // Row index where table headers start (0-based)
      const lastDataRow = headerRowIndex + clients.length;
      const lastColumn = headers.length - 1;
      
      // ADD AUTOFILTER for sorting/filtering - ONLY for the data table, not summary
      worksheet['!autofilter'] = {
        ref: `A${headerRowIndex + 1}:${XLSX.utils.encode_col(lastColumn)}${lastDataRow}`
      };
      
      // Set default sort: Revenue columns sorted highest to lowest
      if (!worksheet['!sort']) worksheet['!sort'] = [];
      worksheet['!sort'].push({
        col: 5,  // Total Revenue column
        desc: true
      });
      
      // Set column widths based on exact specifications
      const setColumnWidths = (headers) => {
        const colWidths = headers.map((header, colIndex) => {
          // Set specific widths based on exact requirements
          switch (header) {
            case 'Client Name':
              return { wch: 65 };
            case 'Country':
              return { wch: 25 };
            case 'Total Rooms':
              return { wch: 25 };
            case 'Manager':
              return { wch: 25 };
            case 'Tier':
              return { wch: 18 };
            case 'Total Revenue':
              return { wch: 25 };
            case 'Last Booking Revenue':
              return { wch: 32 };
            case 'Last Booked':
              return { wch: 25 };
            case 'Time Zone':
              return { wch: 20 };
            case 'Last Booked Conference':
              return { wch: 36 };
            case 'Total RN':
              return { wch: 18 };
            default:
              return { wch: 25 }; // Default fallback
          }
        });
        
        return colWidths;
      };
      
      // Set column widths based on image proportions
      worksheet['!cols'] = setColumnWidths(headers);
      
      // Set row heights - consistent 22 for all rows
      worksheet['!rows'] = styledData.map(() => ({ hpt: 22 }));
      
      // Merge title cells across all 11 columns and 2 rows (vertically centered)
      if (!worksheet['!merges']) worksheet['!merges'] = [];
      worksheet['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 1, c: 10 } });
      
      // Freeze header rows
      worksheet['!freeze'] = { 
        xSplit: 0, 
        ySplit: headerRowIndex + 1
      };
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Client Portfolio');
      
      // Generate filename
      const now = new Date();
      const dateString = now.toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `${portfolioType === 'manager' && activeManager 
        ? `${activeManager}_Client_Portfolio_${dateString}` 
        : `Custom_Portfolio_${dateString}`}.xlsx`;

      // Write styled Excel file
      XLSX.writeFile(workbook, filename, { 
        compression: true,
        bookType: 'xlsx'
      });
      
      showNotification(`Excel report with sortable columns "${filename}" downloaded!`);
      
    } catch (error) {
      console.error('Error exporting styled Excel:', error);
      showNotification('Failed to export Excel file. Please try again.');
    }
  };

  return { exportToExcel };
};

export default usePinnedListExcelExport;
